import {isEmpty} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {CompanyFeaturesEnum} from '../../../../../../libs/shared/core/src/models/api/company/companyModel';
import {TeamModel} from '../../../../../../libs/shared/core/src/models/api/teamModel';
import {getHasLensNegativeFilters} from '../../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {TextBox} from '../../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {
  LinkNavigator,
  useLinkNavigation,
} from '../../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {isDarkSidebarTheme} from '../../../../../../libs/shared/ui/src/theme/themeTypeHelpers';
import {
  LightUserThemeProvider,
  useUserTheme,
} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {LensModel} from '../../../../../models/api/lensModel';
import {PermissionsEnum} from '../../../../../models/api/roleModel';
import {SettingsResourcePagesEnum, SettingsRootPagesEnum} from '../../../../../models/url/models/types';
import {buildSettingsPath} from '../../../../../models/url/settingsUrlHelpers';
import {getFindHasCompanyFeature} from '../../../../../store/data/company/companySelectors';
import {getCustomFieldsForConversationsFlat} from '../../../../../store/data/customFields/customFieldsSelectors';
import {LensesError, LensesFormTypesEnum} from '../../../../../store/data/lenses/lensesHelpers';
import {LensTemplate} from '../../../../../store/data/lenses/saveLensActions';
import {getFirstTeamUserIsAdminOf} from '../../../../../store/data/teams/teamsSelectors';
import {PaletteColorPicker} from '../../../../common/color/paletteColorPicker';
import {DialogWithInfoHeader} from '../../../../common/dialogs/dialogWithInfoHeader';
import {NamespaceDropdownBoxContainer} from '../../../../common/dropdowns/namespace/namespaceDropdownBoxContainer';
import {TagEditorEmoji} from '../../../../common/dropdowns/tags/tagEditorEmoji';
import {EducationBannerContentItem} from '../../../../common/educationBanner/educationBanner';
import {LensFormMultiSelectAssigneesContainer} from '../../../../lenses/form/lensFormMultiSelectAssigneesContainer';
import {LensFormMultiSelectCustomFields} from '../../../../lenses/form/lensFormMultiSelectCustomFields';
import {LensFormMultiSelectExcludeAssigneesContainer} from '../../../../lenses/form/lensFormMultiSelectExcludeAssigneesContainer';
import {LensFormMultiSelectExcludeTags} from '../../../../lenses/form/lensFormMultiSelectExcludeTags';
import {LensFormMultiSelectInboxesContainer} from '../../../../lenses/form/lensFormMultiSelectInboxesContainer';
import {LensFormMultiSelectTags} from '../../../../lenses/form/lensFormMultiSelectTags';
import {parseLensesErrorToFormError} from '../../../../lenses/lensesErrorHelpers';
import {useLensState} from '../../../../lenses/lensesState';

/*
 * Constants.
 */

const createButtonText = <Trans id="lenses-workspace-dialog-create">Create</Trans>;
const createHeaderText = <Trans id="lenses-workspace-dialog-create-title">Create private view</Trans>;
const editButtonText = <Trans id="lenses-workspace-dialog-edit">Save</Trans>;
const editHeaderText = <Trans id="lenses-workspace-dialog-edit-title">Edit private view</Trans>;

/*
 * Props.
 */

export interface LensesWorkspaceDialogBaseProps {
  onRequestClose: () => void;
  userNamespace: string;
  onSubmit: (template: LensTemplate) => Promise<void>;
  initialNamespace?: string;
  initialTagIdsFilter?: ReadonlyArray<number>;
  shouldShowInfoByDefault?: boolean;
}

interface LensesWorkspaceDialogCreateProps {
  isEditing: false;
  lens?: never;
}

interface LensesWorkspaceDialogEditProps {
  isEditing: true;
  lens: LensModel;
  onDelete: () => void;
}

type LensesWorkspaceDialogProps = LensesWorkspaceDialogBaseProps &
  (LensesWorkspaceDialogCreateProps | LensesWorkspaceDialogEditProps);

/*
 * Styles.
 */

const StyledContentWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledSectionDiv = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledTitleDiv = styled.div`
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledHighlightDiv = styled.div`
  margin: 6px 0 10px;

  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: min-content;
  grid-column-gap: 5px;
  align-items: center;
`;

const StyledOptionalFieldDiv = styled.div`
  margin-top: 10px;
`;

/*
 * Component.
 */

export const LensesWorkspaceDialog: FC<LensesWorkspaceDialogProps> = (props) => {
  const {
    isEditing,
    lens,
    onRequestClose,
    userNamespace,
    initialNamespace,
    initialTagIdsFilter,
    shouldShowInfoByDefault,
  } = props;
  const theme = useUserTheme();

  const {
    name,
    onNameChange,
    selectedNamespace,
    selectedInboxIds,
    onSelectInboxIdsChange,
    selectedTagIds,
    onSelectTagIdsChange,
    selectedNotTagIds,
    onSelectNotTagIdsChange,
    noTags,
    onNoTagsChange,
    selectedCustomAttributes,
    onAddCustomField,
    onUpdateCustomAttribute,
    onReplaceCustomField,
    onRemoveCustomField,
    selectedTeammateIds,
    onSelectTeammateIdsChange,
    selectedNotTeammateIds,
    onSelectNotTeammateIdsChange,
    highlightEmoji,
    buildValidTemplate,
    buildValidEditTemplate,
    canSave,
    onNamespaceSelect,
    highlightDisplayValues,
    onEmojiSelect,
    onColorSelect,
    lensFormErrors,
    addLensFormError,
    hasErrorForField,
    setIsSaving,
  } = useLensState({lens, initialNamespace, initialTagIdsFilter});
  const firstTeam = useAppSelector(getFirstTeamUserIsAdminOf);
  const findHasCompanyFeature = useAppSelector(getFindHasCompanyFeature);
  const hasCompanyTeamLenses = findHasCompanyFeature(CompanyFeaturesEnum.LENSES_TEAM);
  const {navigateToLink} = useLinkNavigation();

  const hasLensNegativeFilters = useAppSelector(getHasLensNegativeFilters);
  const customFields = useAppSelector(getCustomFieldsForConversationsFlat);

  const onOkClick = async () => {
    const template = isEditing ? buildValidEditTemplate() : buildValidTemplate();
    if (!template) {
      return;
    }

    try {
      setIsSaving(true);
      await props.onSubmit(template);
      onRequestClose();
    } catch (e) {
      if (e instanceof LensesError) {
        addLensFormError(parseLensesErrorToFormError(e));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const educationBannerTitle = (
    <Trans id="lenses-workspace-dialog-education-header">Why should I use views?</Trans>
  );
  const userLensEducationContent = computeEducationBannerContent(firstTeam, hasCompanyTeamLenses);

  const maybeAddEditDialogProps = () => {
    if (!isEditing) {
      return {};
    }

    return {
      secondaryButtonLabel: <Trans id="lens-dialog-delete">Delete</Trans>,
      onSecondaryClick: props.onDelete,
      secondaryButtonVariant: ButtonVariantsEnum.SECONDARY_DANGER,
    };
  };

  const jsx = (
    <DialogWithInfoHeader
      primaryButtonLabel={isEditing ? editButtonText : createButtonText}
      title={isEditing ? editHeaderText : createHeaderText}
      onPrimaryClick={onOkClick}
      isPrimaryButtonDisabled={!canSave}
      onRequestClose={onRequestClose}
      educationBannerTitle={educationBannerTitle}
      shouldShowInfoByDefault={shouldShowInfoByDefault ?? false}
      learnMoreUrl="https://help.front.com/t/g9h003t/views"
      educationBannerContent={userLensEducationContent}
      leftButton={maybeRenderCreateTeamViewButton(firstTeam, hasCompanyTeamLenses, navigateToLink)}
      height={650}
      {...maybeAddEditDialogProps()}
    >
      <StyledContentWrapperDiv>
        {/* Name input. */}
        <TextBox
          label={<Trans id="lenses-workspace-create-dialog-label-name">Name</Trans>}
          placeholder="Enter name"
          value={name}
          onChange={onNameChange}
          shouldAutoFocus
          isRequired
          isDisabled={false}
          hasError={hasErrorForField(LensesFormTypesEnum.NAME)}
          errorMessage={lensFormErrors[LensesFormTypesEnum.NAME]}
        />
        {/* Highlight (color/emoji) input. */}
        <StyledSectionDiv>
          <StyledTitleDiv>
            <Trans id="lenses-workspace-create-dialog-label-highlight">Color or emoji</Trans>
          </StyledTitleDiv>
          <StyledHighlightDiv>
            <PaletteColorPicker paletteColor={highlightDisplayValues.color} onColorSelect={onColorSelect} />
            <TagEditorEmoji
              emoji={highlightDisplayValues.emoji.emoji}
              skinTone={highlightDisplayValues.emoji.skinTone}
              onEmojiSelect={onEmojiSelect}
              isSelected={Boolean(highlightEmoji)}
            />
          </StyledHighlightDiv>
        </StyledSectionDiv>
        {/* Team picker. */}
        <NamespaceDropdownBoxContainer
          selectedNamespace={selectedNamespace}
          onNamespaceSelect={onNamespaceSelect}
          label={
            <Trans id="lenses-workspace-create-dialog-label-team">
              Show conversations from this workspace
            </Trans>
          }
          placeholder={<Trans id="lenses-workspace-create-dialog-placeholder-team">Select workspace</Trans>}
          // By setting resourceNamespace to the userNamespace,
          //  this dropdown will include all teams the user has the given permission for.
          resourceNamespace={userNamespace}
          permission={PermissionsEnum.READ_INBOX}
          isDisplayOnly={isEditing}
          messageForDisplayOnly={
            <Trans id="lenses-workspace-edit-dialog-team-tooltip">
              Cannot edit the workspace after the view is created.
            </Trans>
          }
          isRequired
          shouldHideUserNamespaceChoice
          shouldHideSandbox
        />
        {/* Inboxes picker. */}
        <LensFormMultiSelectInboxesContainer
          namespace={selectedNamespace}
          selectedIds={selectedInboxIds}
          onSetItemIds={onSelectInboxIdsChange}
          isDisabled={!selectedNamespace}
          hasError={hasErrorForField(LensesFormTypesEnum.INBOXES)}
          errorMessage={lensFormErrors[LensesFormTypesEnum.INBOXES]}
          lensId={lens?.id}
        />
        {/* Tags picker. */}
        <StyledOptionalFieldDiv>
          <LensFormMultiSelectTags
            inboxIds={selectedInboxIds}
            namespace={selectedNamespace}
            selectedIds={selectedTagIds}
            onSetItemIds={onSelectTagIdsChange}
            hasError={hasErrorForField(LensesFormTypesEnum.TAGS)}
            errorMessage={lensFormErrors[LensesFormTypesEnum.TAGS]}
            idsToDisable={selectedNotTagIds}
            noTags={noTags}
          />
        </StyledOptionalFieldDiv>
        {/* Exclude Tags picker. */}
        {hasLensNegativeFilters && (
          <StyledOptionalFieldDiv>
            <LensFormMultiSelectExcludeTags
              inboxIds={selectedInboxIds}
              namespace={selectedNamespace}
              selectedIds={selectedNotTagIds}
              onSetItemIds={onSelectNotTagIdsChange}
              noTags={noTags}
              onSetNoTags={onNoTagsChange}
              isDisabled={isEmpty(selectedInboxIds)}
              hasError={hasErrorForField(LensesFormTypesEnum.NOT_TAGS)}
              errorMessage={lensFormErrors[LensesFormTypesEnum.NOT_TAGS]}
              idsToDisable={selectedTagIds}
            />
          </StyledOptionalFieldDiv>
        )}
        {/* Assignees picker. */}
        {hasLensNegativeFilters ? (
          <StyledOptionalFieldDiv>
            <LensFormMultiSelectExcludeAssigneesContainer
              selectedIds={selectedTeammateIds}
              onSetItemIds={onSelectTeammateIdsChange}
              selectedExcludedIds={selectedNotTeammateIds}
              onSetExcludedItemIds={onSelectNotTeammateIdsChange}
              isDisabled={isEmpty(selectedInboxIds)}
              hasError={hasErrorForField(LensesFormTypesEnum.ASSIGNEES)}
              errorMessage={lensFormErrors[LensesFormTypesEnum.ASSIGNEES]}
            />
          </StyledOptionalFieldDiv>
        ) : (
          <StyledOptionalFieldDiv>
            <LensFormMultiSelectAssigneesContainer
              selectedIds={selectedTeammateIds}
              onSetItemIds={onSelectTeammateIdsChange}
              isDisabled={isEmpty(selectedInboxIds)}
              hasError={hasErrorForField(LensesFormTypesEnum.ASSIGNEES)}
              errorMessage={lensFormErrors[LensesFormTypesEnum.ASSIGNEES]}
            />
          </StyledOptionalFieldDiv>
        )}
        {/* Custom attributes picker. */}
        <StyledOptionalFieldDiv>
          <LensFormMultiSelectCustomFields
            customAttributes={selectedCustomAttributes}
            customFields={customFields}
            onAddCustomField={onAddCustomField}
            onUpdateCustomAttribute={onUpdateCustomAttribute}
            onReplaceCustomField={onReplaceCustomField}
            onRemoveCustomField={onRemoveCustomField}
            isDisabled={isEmpty(selectedInboxIds)}
            hasError={hasErrorForField(LensesFormTypesEnum.CUSTOM_FIELDS)}
            errorMessage={lensFormErrors[LensesFormTypesEnum.CUSTOM_FIELDS]}
          />
        </StyledOptionalFieldDiv>
      </StyledContentWrapperDiv>
    </DialogWithInfoHeader>
  );

  // In the dark sidebar theme, workspace dialogs should still render in light mode.
  if (isDarkSidebarTheme(theme)) {
    return <LightUserThemeProvider>{jsx}</LightUserThemeProvider>;
  }

  return jsx;
};

const itemOne = {
  content: (
    <Trans id="lenses-workspace-dialog-education-content-one">
      You can monitor important shared tags and assignees across multiple inboxes all in one view. Create a
      shared view in settings if you want your teammates to have the same working queues.
    </Trans>
  ),
  key: 'lenses-workspace-dialog-education-content-one',
};

const itemTwo = {
  content: (
    <Trans id="lenses-workspace-dialog-education-content-two">
      You can monitor important shared tags and assignees across multiple inboxes all in one view.
    </Trans>
  ),
  key: 'lenses-workspace-dialog-education-content-two',
};
const itemThree = {
  content: (
    <Trans id="lenses-workspace-dialog-education-content-three">
      You can track conversations assigned to specific teammates.
    </Trans>
  ),
  key: 'lenses-workspace-dialog-education-content-three',
};

const itemFour = {
  content: (
    <Trans id="lenses-workspace-dialog-education-content-four">
      You can quickly find all high priority conversations based on a single tag (e.g. Urgent) across multiple
      inboxes.
    </Trans>
  ),
  key: 'lenses-workspace-dialog-education-content-four',
};

const computeEducationBannerContent = (
  firstTeamUserIsAdminOf: TeamModel | undefined,
  hasTeamLenses: boolean,
): ReadonlyArray<EducationBannerContentItem> => {
  if (firstTeamUserIsAdminOf && hasTeamLenses) {
    return [itemOne, itemThree];
  }

  if (firstTeamUserIsAdminOf) {
    return [itemTwo, itemThree];
  }

  // Any non-admin
  return [itemTwo, itemFour];
};

const maybeRenderCreateTeamViewButton = (
  firstTeamUserIsAdminOf: TeamModel | undefined,
  hasTeamLenses: boolean,
  navigateToLink: LinkNavigator,
) => {
  if (!hasTeamLenses || !firstTeamUserIsAdminOf) {
    return null;
  }

  return (
    <Button
      variant={ButtonVariantsEnum.LINK}
      size={VisualSizesEnum.SMALL}
      onClick={() => navigateToLink(buildSettingsNewLensUrl(firstTeamUserIsAdminOf))}
    >
      <Trans id="lens-create-team-education-banner">Create shared view</Trans>
    </Button>
  );
};

const buildSettingsNewLensUrl = (firstTeamUserIsAdminOf: TeamModel) =>
  buildSettingsPath({
    namespace: firstTeamUserIsAdminOf.namespace,
    rootPage: SettingsRootPagesEnum.LENSES,
    resourcePage: SettingsResourcePagesEnum.NEW,
    lensId: undefined,
    tagIdsFilter: undefined,
  });
